/**
 * Micro-Class Builder types — class creation, pipeline, LLM prompts, TTS, library.
 */

// ── Engine Types ────────────────────────────────────────────────────

export type EngineTypeId = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15;

export interface EngineType {
  id: EngineTypeId;
  name: string;
  description: string;
  interaction_types: InteractionType[];
}

export type InteractionType =
  | 'mcq'
  | 'free_text'
  | 'timed_text'
  | 'ranked_list'
  | 'video_capture'
  | 'dialogue_simulation';

export const ENGINE_TYPES: EngineType[] = [
  { id: 1,  name: 'Pattern Recognition',      description: 'Identify recurring patterns in data or scenarios',    interaction_types: ['mcq'] },
  { id: 2,  name: 'Trade-Off Analysis',        description: 'Evaluate competing priorities and constraints',       interaction_types: ['mcq', 'free_text'] },
  // 2026-10-06 (Tools): 3 and 5 carried the wrong names; the ids were right
  // (their skills in PLOT_SKILL_MAP are Gap Analysis's and Assumption Testing's).
  // Names now match the server (src/micro_class/engine_types.py).
  { id: 3,  name: 'Gap Analysis',              description: 'Find what is missing or inconsistent in the information', interaction_types: ['timed_text'] },
  { id: 4,  name: 'Root Cause',                description: 'Drill down to underlying causes of problems',        interaction_types: ['free_text'] },
  { id: 5,  name: 'Assumption Testing',        description: 'Surface and challenge hidden assumptions',            interaction_types: ['mcq', 'free_text'] },
  { id: 6,  name: 'Adaptation',                description: 'Adjust approach in response to changing conditions',  interaction_types: ['timed_text'] },
  { id: 7,  name: 'Rapid Ideation',            description: 'Generate creative ideas under time pressure',        interaction_types: ['timed_text'] },
  { id: 8,  name: 'Data Synthesis',             description: 'Combine multiple data sources into insight',         interaction_types: ['free_text'] },
  { id: 9,  name: 'Prioritization',            description: 'Rank items by impact and urgency',                   interaction_types: ['ranked_list', 'free_text'] },
  { id: 10, name: 'Messaging Framework',       description: 'Craft structured communications for audiences',       interaction_types: ['free_text'] },
  { id: 11, name: 'Live Delivery',             description: 'Practice live presentation and speaking skills',      interaction_types: ['video_capture'] },
  { id: 12, name: 'Conversational Response',   description: 'Navigate dialogue with AI-simulated counterparts',    interaction_types: ['dialogue_simulation'] },
  { id: 13, name: 'Self-Reflection',           description: 'Reflect on personal decision-making patterns',        interaction_types: ['free_text'] },
  { id: 14, name: 'Planning & Coordination',   description: 'Coordinate multi-step plans across stakeholders',     interaction_types: ['free_text', 'mcq'] },
  { id: 15, name: 'Audience-Centric',          description: 'Tailor content and approach for specific audiences',  interaction_types: ['free_text'] },
];

// ── Plot → Skills → Frameworks (from Micro-Class_Design_Master.xlsx) ──

export interface PlotSkillMapping {
  id: EngineTypeId;
  skills: string[];
  primaryFrameworks: string;
  supportingFrameworks: string;
}

export const PLOT_SKILL_MAP: PlotSkillMapping[] = [
  { id: 1,  skills: ['Spotting Trends','Noticing Reactions','Seeing The Big Picture','Reading Body Language','Gauging Opinions','Mapping Influence','Prioritizing Data'], primaryFrameworks: 'Influence Principles, SWOT Analysis, Second-Order Thinking, Six Thinking Hats', supportingFrameworks: '—' },
  { id: 2,  skills: ['Making Trade-Offs','Assessing Risks','Weighing Risk and Reward','Ranking Priorities','Identifying Essentials','Finding New Uses','Mapping Effects'], primaryFrameworks: 'Eisenhower Matrix, SCAMPER, SWOT Analysis, Second-Order Thinking', supportingFrameworks: 'MECE Framework' },
  { id: 3,  skills: ['Finding The Gaps','Checking Consistency','Identifying Essentials','Spotting Flawed Reasoning','Verifying Facts','Detecting Bias','Evaluating Credibility'], primaryFrameworks: 'Cognitive Biases Toolkit, MECE Framework, SWOT Analysis', supportingFrameworks: '5 Whys Analysis, Socratic Method' },
  { id: 4,  skills: ['Drawing Conclusions','Explaining The Reasoning','Tracing Foundations','Mapping Effects','Harmonizing Data','Correcting Course','Testing Significance'], primaryFrameworks: '5 Whys Analysis, First Principles Thinking, Issue Trees, PDCA Cycle, Second-Order Thinking', supportingFrameworks: '—' },
  { id: 5,  skills: ['Challenging Claims','Surfacing Assumptions','Stress-Testing Ideas','Spotting Assumptions','Challenging The Consensus'], primaryFrameworks: 'Cognitive Biases Toolkit, First Principles Thinking, Inversion, Lateral Thinking, Socratic Method', supportingFrameworks: '—' },
  { id: 6,  skills: ['Dealing With Ambiguity','Adapting To Constraints','Pivoting Mid-Conversation','Switching Gears','Shifting Priorities','Navigating Setbacks','Dropping Old Ways'], primaryFrameworks: 'Eisenhower Matrix, Growth Mindset, OODA Loop', supportingFrameworks: 'Situational Leadership' },
  { id: 7,  skills: ['Ideating Rapidly','Sketching Rapidly','Breaking Rules','Branching Out','Flipping The Problem','Creating Hybrids','Reusing Parts'], primaryFrameworks: 'Brainstorming Rules, Inversion, Lateral Thinking, SCAMPER', supportingFrameworks: '—' },
  { id: 8,  skills: ['Harmonizing Data','Extracting Lessons','Exploring Angles','Borrowing Tactics','Thinking Across Fields','Merging Ideas','Pairing Unlikely Elements'], primaryFrameworks: 'Six Thinking Hats', supportingFrameworks: 'SCAMPER' },
  { id: 9,  skills: ['Identifying Essentials','Finding New Uses','Ranking Priorities','Adapting To Constraints','Exposing Weak Spots'], primaryFrameworks: 'Eisenhower Matrix, Inversion, SCAMPER, SWOT Analysis', supportingFrameworks: 'MECE Framework' },
  { id: 10, skills: ['Tailoring The Message','Pacing Delivery','Structuring The Flow','Setting The Starting Point','Defining The Gap','Making It Stick','Using Visuals','Presenting Virtually','Reading Between The Lines','Crafting Narratives','Boiling Down Ideas'], primaryFrameworks: "Influence Principles, MECE Framework, Monroe's Motivated Sequence, Pyramid Principle, STAR Method", supportingFrameworks: '—' },
  { id: 11, skills: ['Speaking Cleanly','Conveying Authority','Managing Tone','Pacing Delivery','Using Visuals','Presenting Virtually','Projecting Confidence','Keeping Calm Under Pressure','Staying On Track','Resetting After Pressure','Reading Body Language','Using Gestures'], primaryFrameworks: '—', supportingFrameworks: 'SBI Model, Motivational Interviewing' },
  { id: 12, skills: ['Delivering Feedback','Pursuing Feedback','Committing To Next Steps','Defusing Tension','Merging Ideas','Handling Pushback','Anchoring On Common Ground','Aligning Viewpoints','Tailoring The Message','Reading Between The Lines','Noticing Reactions','Navigating Setbacks','Adapting To Constraints','Resetting After Pressure','Showing Engagement'], primaryFrameworks: 'Growth Mindset, Influence Principles, Radical Candor, SBI Model, Motivational Interviewing', supportingFrameworks: "Monroe's Motivated Sequence, Johari Window" },
  { id: 13, skills: ['Reflecting On Actions','Owning Mistakes','Checking Against Standards','Committing To Next Steps','Showing Progress','Taking On Hard Problems','Applying The Lesson','Driving To Completion','Getting On Board','Resetting After Pressure','Parking Distractions','Staying On Track','Acting On Advice'], primaryFrameworks: 'Eisenhower Matrix, Growth Mindset, PDCA Cycle, Johari Window', supportingFrameworks: '—' },
  { id: 14, skills: ['Setting Clear Goals','Clarifying Roles','Following Up','Creating Options','Taking Ownership','Uncovering Interests','Mapping Systems','Tracking Origins','Identifying Relevance','Anchoring Logic'], primaryFrameworks: 'Issue Trees, MECE Framework, Servant Leadership', supportingFrameworks: '5 Whys Analysis' },
  { id: 15, skills: ['Translating Jargon','Writing Clear Instructions','Writing For Action','Identifying Key Points','Sharpening The Message','Speaking Cleanly','Articulating Clearly','Thinking Like The User','Creating Analogies','Asking What-Ifs','Painting The Future','Naming Constraints','Applying Proven Models'], primaryFrameworks: "Lateral Thinking, Monroe's Motivated Sequence, Pyramid Principle, Six Thinking Hats", supportingFrameworks: 'MECE Framework, SBI Model' },
];

// ── Skill Tree (5 pillars → L2 → L3 → L4 skills) ────────────────

export interface SkillTreePillar {
  name: string;
  pillarId: PillarId;
  domains: { name: string; skillGroups: { name: string; skills: string[] }[] }[];
}

export const SKILL_TREE: SkillTreePillar[] = [
  { name: 'CHARACTER', pillarId: 'character', domains: [
    { name: 'Self-Mastery', skillGroups: [
      { name: 'Leveraging Experience', skills: ['Reflecting On Actions','Extracting Lessons','Correcting Course'] },
      { name: 'Assessing Performance', skills: ['Checking Against Standards','Owning Mistakes','Stating Boundaries'] },
      { name: 'Maintaining Composure', skills: ['Managing Tone','Staying On Track','Resetting After Pressure'] },
    ]},
    { name: 'Adaptability', skillGroups: [
      { name: 'Managing Priorities', skills: ['Shifting Priorities','Parking Distractions','Switching Gears'] },
      { name: 'Developing Resilience', skills: ['Keeping Calm Under Pressure','Dealing With Ambiguity','Driving To Completion'] },
      { name: 'Learning Quickly', skills: ['Getting On Board','Dropping Old Ways','Applying The Lesson'] },
    ]},
    { name: 'Growth Agility', skillGroups: [
      { name: 'Processing Feedback', skills: ['Confirming Understanding','Asking For Clarity','Committing To Next Steps'] },
      { name: 'Receiving Coaching', skills: ['Acting On Advice','Adapting Fast','Showing Progress'] },
      { name: 'Seeking Challenges', skills: ['Taking On Hard Problems','Navigating Setbacks','Pursuing Feedback'] },
    ]},
  ]},
  { name: 'COLLABORATION', pillarId: 'collaboration', domains: [
    { name: 'Relational Intelligence', skillGroups: [
      { name: 'Building Rapport', skills: ['Reading Body Language','Bridging Cultures','Expressing Curiosity'] },
      { name: 'Drawing Others Out', skills: ['Asking Open Questions','Managing Dialogue','Encouraging Elaboration'] },
      { name: 'Building Trust', skills: ['Taking Ownership','Delivering Feedback','Repairing Trust'] },
    ]},
    { name: 'Social Influence', skillGroups: [
      { name: 'Analyzing Stakeholders', skills: ['Uncovering Interests','Mapping Influence','Building Alliances'] },
      { name: 'Resolving Conflict', skills: ['Defusing Tension','Reframing The Conflict','Reaching Resolution'] },
      { name: 'Negotiating Strategically', skills: ['Creating Options','Finding Mutual Gains','Locking In Agreement'] },
    ]},
    { name: 'Collective Performance', skillGroups: [
      { name: 'Orchestrating Groups', skills: ['Setting Clear Goals','Sharing The Floor','Steering Focus'] },
      { name: 'Aligning The Group', skills: ['Gauging Opinions','Anchoring On Common Ground','Handling Pushback'] },
      { name: 'Coordinating Operations', skills: ['Clarifying Roles','Delegating Effectively','Following Up'] },
    ]},
  ]},
  { name: 'COMMUNICATION', pillarId: 'communication', domains: [
    { name: 'Message Crafting', skillGroups: [
      { name: 'Clarifying Concepts', skills: ['Identifying Key Points','Translating Jargon','Writing Clear Instructions'] },
      { name: 'Organizing Messages', skills: ['Structuring The Flow','Writing For Action','Tailoring The Message'] },
      { name: 'Cutting To The Point', skills: ['Speaking Cleanly','Sharpening The Message','Making It Stick'] },
    ]},
    { name: 'Delivery Dynamics', skillGroups: [
      { name: 'Commanding Presence', skills: ['Projecting Confidence','Using Gestures','Owning The Space'] },
      { name: 'Mastering Voice', skills: ['Conveying Authority','Pacing Delivery','Articulating Clearly'] },
      { name: 'Mastering Presentation', skills: ['Presenting Virtually','Using Visuals','Crafting Narratives'] },
    ]},
    { name: 'Active Listening', skillGroups: [
      { name: 'Picking Up Signals', skills: ['Noticing Reactions','Showing Engagement','Reading The Room'] },
      { name: 'Taking Perspective', skills: ['Reading Between The Lines','Acknowledging Positions','Spotting Assumptions'] },
      { name: 'Reading And Adjusting', skills: ['Matching Tone','Aligning Viewpoints','Pivoting Mid-Conversation'] },
    ]},
  ]},
  { name: 'CREATIVITY', pillarId: 'creativity', domains: [
    { name: 'Idea Generation', skillGroups: [
      { name: 'Generating Options', skills: ['Ideating Rapidly','Exploring Angles','Branching Out'] },
      { name: 'Shifting Perspectives', skills: ['Thinking Like The User','Flipping The Problem','Thinking Across Fields'] },
      { name: 'Sparking Ideas', skills: ['Breaking Rules','Asking What-Ifs','Challenging The Consensus'] },
    ]},
    { name: 'Idea Fusion', skillGroups: [
      { name: 'Cross-Pollinating Ideas', skills: ['Creating Analogies','Borrowing Tactics','Applying Proven Models'] },
      { name: 'Blending Concepts', skills: ['Merging Ideas','Pairing Unlikely Elements','Creating Hybrids'] },
      { name: 'Repurposing Components', skills: ['Boiling Down Ideas','Reusing Parts','Finding New Uses'] },
    ]},
    { name: 'Applied Innovation', skillGroups: [
      { name: 'Validating Concepts', skills: ['Identifying Essentials','Sketching Rapidly','Exposing Weak Spots'] },
      { name: 'Integrating Constraints', skills: ['Naming Constraints','Adapting To Constraints','Making Trade-Offs'] },
      { name: 'Showing The Impact', skills: ['Setting The Starting Point','Painting The Future','Defining The Gap'] },
    ]},
  ]},
  { name: 'CRITICAL THINKING', pillarId: 'critical_thinking', domains: [
    { name: 'Data Interpretation', skillGroups: [
      { name: 'Finding What Matters', skills: ['Identifying Relevance','Testing Significance','Ranking Priorities'] },
      { name: 'Validating Sources', skills: ['Tracking Origins','Detecting Bias','Evaluating Credibility'] },
      { name: 'Connecting The Dots', skills: ['Spotting Trends','Seeing The Big Picture','Mapping Systems'] },
    ]},
    { name: 'Critical Analysis', skillGroups: [
      { name: 'Examining Evidence', skills: ['Challenging Claims','Verifying Facts','Finding The Gaps'] },
      { name: 'Breaking Down Logic', skills: ['Tracing Foundations','Explaining The Reasoning','Spotting Flawed Reasoning'] },
      { name: 'Testing Assumptions', skills: ['Surfacing Assumptions','Checking Consistency','Stress-Testing Ideas'] },
    ]},
    { name: 'Decision Quality', skillGroups: [
      { name: 'Building The Case', skills: ['Prioritizing Data','Harmonizing Data','Drawing Conclusions'] },
      { name: 'Evaluating Impact', skills: ['Mapping Effects','Assessing Risks','Weighing Risk and Reward'] },
      { name: 'Forming Verdicts', skills: ['Anchoring Logic','Stating Decisions','Defending Rationale'] },
    ]},
  ]},
];

// ── Non-assessable skills (24 of 135 L4 skills not covered by any plot) ──

export const NON_ASSESSABLE_SKILLS: Set<string> = new Set([
  'Acknowledging Positions','Adapting Fast','Asking For Clarity','Asking Open Questions',
  'Bridging Cultures','Building Alliances','Confirming Understanding','Defending Rationale',
  'Delegating Effectively','Encouraging Elaboration','Expressing Curiosity','Finding Mutual Gains',
  'Locking In Agreement','Managing Dialogue','Matching Tone','Owning The Space',
  'Reaching Resolution','Reading The Room','Reframing The Conflict','Repairing Trust',
  'Sharing The Floor','Stating Boundaries','Stating Decisions','Steering Focus',
]);

/** All 111 assessable L4 skills (derived from PLOT_SKILL_MAP) */
export const ASSESSABLE_SKILLS: Set<string> = new Set(
  PLOT_SKILL_MAP.flatMap((p) => p.skills),
);

/** Given selected L4 skills, return the plot IDs that assess at least one of them */
export function getPlotsForSkills(selectedSkills: string[]): EngineTypeId[] {
  if (selectedSkills.length === 0) return [];
  const selected = new Set(selectedSkills);
  return PLOT_SKILL_MAP
    .filter((p) => p.skills.some((s) => selected.has(s)))
    .map((p) => p.id);
}

// ── Language ────────────────────────────────────────────────────────

export type ContentLanguage = 'en' | 'id';

export const LANGUAGE_OPTIONS: { value: ContentLanguage; label: string; flag: string }[] = [
  { value: 'en', label: 'English',           flag: '🇬🇧' },
  { value: 'id', label: 'Bahasa Indonesia',  flag: '🇮🇩' },
];

// ── Difficulty ──────────────────────────────────────────────────────

export type DifficultyLevel = 'beginner' | 'intermediate' | 'advanced';

export const DIFFICULTY_OPTIONS: { value: DifficultyLevel; label: string; colour: string }[] = [
  { value: 'beginner',     label: 'Beginner',     colour: 'text-emerald-600 bg-emerald-50' },
  { value: 'intermediate', label: 'Intermediate', colour: 'text-amber-600 bg-amber-50' },
  { value: 'advanced',     label: 'Advanced',     colour: 'text-red-600 bg-red-50' },
];

// ── Duration presets ────────────────────────────────────────────────

export const DURATION_OPTIONS: { value: number; label: string; emoji: string }[] = [
  { value: 3,  label: '3 min',  emoji: '⚡' },
  { value: 5,  label: '5 min',  emoji: '🎯' },
  { value: 7,  label: '7 min',  emoji: '📝' },
  { value: 10, label: '10 min', emoji: '🧠' },
  { value: 15, label: '15 min', emoji: '🏋️' },
];

// ── Industry options ───────────────────────────────────────────────

export const INDUSTRY_OPTIONS: string[] = [
  'Technology',
  'Financial Services',
  'Healthcare & Pharma',
  'Education',
  'E-Commerce & Retail',
  'Manufacturing',
  'Energy & Utilities',
  'Telecommunications',
  'Media & Entertainment',
  'Government & Public Sector',
  'Hospitality & Tourism',
  'Real Estate & Construction',
  'Transportation & Logistics',
  'Agriculture & Food',
  'Legal & Professional Services',
  'Non-Profit & NGO',
  'Automotive',
  'Aerospace & Defence',
  'Insurance',
  'Mining & Resources',
  'Fashion & Luxury',
  'Sports & Fitness',
  'Consulting',
  'Startup / General',
];

// ── Function options ───────────────────────────────────────────────

export const FUNCTION_OPTIONS: string[] = [
  'Operations',
  'Sales',
  'Marketing',
  'Finance & Accounting',
  'Human Resources',
  'Engineering',
  'Product Management',
  'Customer Success',
  'Strategy & Planning',
  'Data & Analytics',
  'Legal & Compliance',
  'Research & Development',
  'Supply Chain',
  'IT & Infrastructure',
  'Design & Creative',
  'Business Development',
  'Project Management',
  'Quality Assurance',
  'Communications & PR',
  'Executive Leadership',
  'General / Cross-Functional',
];

// ── Playbook Pillars ────────────────────────────────────────────────

export type PillarId = 'character' | 'collaboration' | 'communication' | 'creativity' | 'critical_thinking';

export const PILLARS: { id: PillarId; label: string; colour: string }[] = [
  { id: 'character',        label: 'Character',        colour: 'bg-[#FF0080]' },  // magenta/pink
  { id: 'collaboration',    label: 'Collaboration',    colour: 'bg-[#00FFFF]' },  // cyan
  { id: 'communication',    label: 'Communication',    colour: 'bg-[#0080FF]' },  // blue
  { id: 'creativity',       label: 'Creativity',       colour: 'bg-[#80FF00]' },  // lime green
  { id: 'critical_thinking', label: 'Critical Thinking', colour: 'bg-[#FFFF00]' },  // yellow
];

/**
 * Pillar colour palette — matches Flutter app (app_colors.dart).
 *   Character  #FF0080 (magenta)   Collaboration #00FFFF (cyan)
 *   Communication #0080FF (blue)   Creativity #80FF00 (lime)
 *   Critical Thinking #FFFF00 (yellow)
 */
export const PILLAR_COLORS: Record<PillarId, {
  bg: string; text: string; pillBg: string; pillText: string; hover: string; border: string; dot: string;
}> = {
  character:         { bg: 'bg-[#FF0080]', text: 'text-[#FF0080]', pillBg: 'bg-[#FF0080]/15', pillText: 'text-[#CC0066]  dark:text-[#FF4DA6]', hover: 'hover:bg-[#FF0080]/10', border: 'border-[#FF0080]/30', dot: '#FF0080' },
  collaboration:     { bg: 'bg-[#00FFFF]', text: 'text-[#00B3B3]', pillBg: 'bg-[#00FFFF]/15', pillText: 'text-[#008080]  dark:text-[#00FFFF]', hover: 'hover:bg-[#00FFFF]/10', border: 'border-[#00FFFF]/30', dot: '#00FFFF' },
  communication:     { bg: 'bg-[#0080FF]', text: 'text-[#0080FF]', pillBg: 'bg-[#0080FF]/15', pillText: 'text-[#0060CC]  dark:text-[#4DA6FF]', hover: 'hover:bg-[#0080FF]/10', border: 'border-[#0080FF]/30', dot: '#0080FF' },
  creativity:        { bg: 'bg-[#80FF00]', text: 'text-[#4D9900]', pillBg: 'bg-[#80FF00]/15', pillText: 'text-[#4D9900]  dark:text-[#A6FF4D]', hover: 'hover:bg-[#80FF00]/10', border: 'border-[#80FF00]/30', dot: '#80FF00' },
  critical_thinking: { bg: 'bg-[#FFFF00]', text: 'text-[#999900]', pillBg: 'bg-[#FFFF00]/15', pillText: 'text-[#808000]  dark:text-[#FFFF66]', hover: 'hover:bg-[#FFFF00]/10', border: 'border-[#FFFF00]/30', dot: '#FFFF00' },
};

/** Reverse lookup: L4 skill name → pillarId (built once from SKILL_TREE) */
export const SKILL_TO_PILLAR: Record<string, PillarId> = (() => {
  const map: Record<string, PillarId> = {};
  for (const pillar of SKILL_TREE) {
    for (const domain of pillar.domains) {
      for (const sg of domain.skillGroups) {
        for (const skill of sg.skills) {
          map[skill] = pillar.pillarId;
        }
      }
    }
  }
  return map;
})();

// ── Micro-Class Entity ──────────────────────────────────────────────

export type MicroClassStatus = 'draft' | 'generating' | 'generated' | 'audio_ready' | 'review' | 'published' | 'retired';

export interface MicroClassMetadata {
  skills_assessed: string[];
  difficulty: DifficultyLevel;
  duration_minutes: number;
  industry?: string;
  functional_context?: string;
  frameworks?: string[];
  pillar?: PillarId;
}

export interface MicroClass {
  id: string;
  title: string;
  description: string;
  engine_type: EngineTypeId;
  metadata: MicroClassMetadata;
  sections: MicroClassSection[];
  status: MicroClassStatus;
  version: number;
  created_at: string;
  updated_at: string;
  created_by?: string;
  published_at?: string;
}

export interface MicroClassListItem {
  id: string;
  title: string;
  language?: ContentLanguage;
  engine_type: EngineTypeId;
  difficulty: DifficultyLevel;
  duration_minutes: number;
  skills: string[];
  skills_assessed?: string[];           // original L4 names (Title Case)
  pillar?: PillarId;
  pillars?: string[];                   // all pillars from catalogue
  audit_gate?: 'green' | 'amber' | 'red' | '';  // audit health indicator
  status: MicroClassStatus;
  version: number;
  discovery_mode?: boolean;              // whether the class was created in discovery mode
  has_content?: boolean;                 // true if generated_content exists
  has_audio?: boolean;                   // true if audio was generated
  generation_model?: string;             // e.g. "claude-sonnet-4-6"
  created_at: string;
  /** Who made it (email); asked of Scoring 10 Oct, absent until then. */
  created_by?: string | null;
  /** university_students | professionals | both; asked of Scoring 10 Oct. */
  audience?: string | null;
  updated_at: string;
  prompt_used?: string;                  // LLM generation prompt
  generated_content?: Record<string, unknown>; // raw LLM output
  content?: {                            // embedded content from catalogue
    engineType?: number;
    sections?: Record<string, unknown>[];
    metadata?: Record<string, unknown>;
  };
}

// ── Sections ────────────────────────────────────────────────────────

export type SectionType = 'setup' | 'nudge_card' | 'challenge' | 'response' | 'feedback';

export interface MicroClassSection {
  type: SectionType;
  content: Record<string, unknown>;
}

export interface SetupSection {
  scenario: {
    context: string;
    framing: string;
    constraints: string[];
  };
  stakeholders?: string[];
  time_context?: string;
  media?: { type: string; url: string }[];
}

export interface NudgeCard {
  framework_name: string;
  core_question: string;
  first_step: string;
  key_reminder: string;
}

export interface ChallengeSection {
  task_description: string;
  success_criteria: string[];
  stimulus?: string;
  situation?: string;
  model_answer: {
    key_points: string[];
    exemplar: string;
    common_misses: string[];
  };
}

export interface MCQOption {
  id: string;
  text: string;
  is_correct: boolean;
  explanation: string;
}

export interface Interaction {
  type: InteractionType;
  prompt: string;
  // MCQ-specific
  options?: MCQOption[];
  multi_select?: boolean;
  // Free-text-specific
  min_length?: number;
  max_length?: number;
  // Timed-text-specific
  duration_seconds?: number;
  counting_metric?: 'words' | 'ideas' | 'sentences';
  // Ranked-list-specific
  items?: string[];
  optimal_order?: string[];
  top_n?: number;
  // Video-capture-specific
  mode?: 'audio' | 'video' | 'choice';
  max_duration_seconds?: number;
  prep_timer_seconds?: number;
  // Dialogue-simulation-specific
  persona?: string;
  max_turns?: number;
  system_prompt?: string;
}

export interface FeedbackComponent {
  type: 'score_summary' | 'strengths' | 'gaps' | 'nudge_card' | 'text';
  content?: string;
}

// ── Class Creation Wizard ───────────────────────────────────────────

// 2026-06-13 MC content-gen hardening wave (Nino).
export type AudienceTarget = 'university_students' | 'professionals' | 'both';
export type ToneRegister = 'default' | 'formal' | 'direct' | 'warm';
export type CulturalContext = 'general' | 'sea' | 'indonesia' | 'singapore' | 'eu' | 'us';

import type { ImageOverride } from '@/lib/imageLibrary';

export interface ClassCreationForm {
  // Step 1: Topic
  language: ContentLanguage;
  title: string;
  description: string;
  engine_type: EngineTypeId | null;
  difficulty: DifficultyLevel;
  duration_minutes: number;
  skills_assessed: string[];
  industry: string;
  functional_context: string;
  frameworks: string[];
  nudge_card_id: string | null;
  // ── 2026-06-13 hardening wave — new wizard inputs ───────────────────
  audience: AudienceTarget;
  tone_register: ToneRegister;
  cultural_context: CulturalContext;
  avoid_topics: string[];
  // Step 2: Script (auto-generated)
  generated_content: Record<string, unknown> | null;
  prompt_used: string;
  // Step 3: Audio
  audio_voice_id: string;
  audio_generated: boolean;
  audio_url: string | null;
  // Step 4: Review
  validation_passed: boolean;
  validation_errors: ValidationError[];
  // Image Library (6 Oct): a scene picked by hand in the Images block;
  // null = automatic (the library's rules).
  image_override: ImageOverride | null;
}

export interface ValidationError {
  field: string;
  message: string;
  severity: 'error' | 'warning' | 'info';
}

// ── Content Pipeline ────────────────────────────────────────────────

export type PipelineJobStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'validating' | 'cancelled';

export interface PipelineJob {
  id: string;
  class_id: string;
  class_title: string;
  stage: 'script_generation' | 'audio_generation' | 'validation' | 'publishing';
  status: PipelineJobStatus;
  progress: number; // 0-100
  error_message?: string;
  retry_count: number;
  created_at: string;
  updated_at: string;
  completed_at?: string;
  duration_seconds?: number;
}

export interface PipelineStats {
  total_jobs: number;
  pending: number;
  processing: number;
  completed: number;
  failed: number;
  cancelled: number;
  avg_duration_seconds: number;
  success_rate: number;
}

// ── LLM Prompt Management ───────────────────────────────────────────

export interface PromptTemplate {
  id: string;
  name: string;
  description: string;
  template: string;
  variables: PromptVariable[];
  model: string;
  max_tokens: number;
  temperature: number;
  version: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface PromptVariable {
  name: string;
  description: string;
  required: boolean;
  default_value?: string;
}

export interface PromptTestResult {
  prompt_id: string;
  input_variables: Record<string, string>;
  rendered_prompt: string;
  response: string;
  model: string;
  tokens_used: number;
  latency_ms: number;
  created_at: string;
}

export interface LLMProvider {
  id: string;
  name: string;
  model: string;
  status: 'active' | 'inactive' | 'error';
  rate_limit: number;
  tokens_used_today: number;
  cost_today: number;
  avg_latency_ms: number;
}

// ── TTS Configuration ───────────────────────────────────────────────

export interface TTSVoice {
  id: string;
  name: string;
  language: string;
  accent: string;
  gender: 'male' | 'female' | 'neutral';
  tier?: 'neural2' | 'wavenet' | 'standard';
  preview_url?: string;
  is_default: boolean;
}

export interface TTSConfig {
  default_voice_id: string;
  speed: number; // 0.5 – 2.0
  format: 'mp3' | 'wav' | 'aac';
  sample_rate: number;
  provider: string;
  enabled: boolean;
}

// ── Tab enum ────────────────────────────────────────────────────────

export type MCBTab =
  | 'create'
  | 'library';

// ── Engine Interaction Map (mirrors playbook-backend/schemas/engine_interaction_map.json) ──

interface EngineInteractionInfo {
  primaryInteraction: string;
  secondaryInteraction: string | null;
  interactionConfig: Record<string, unknown>;
  dataCaptured: string[];
}

const ENGINE_INTERACTION_MAP: Record<number, EngineInteractionInfo> = {
  1:  { primaryInteraction: 'mcq', secondaryInteraction: null, interactionConfig: { mcqType: 'single_select', optionCount: 5, includeVisualStimulus: true }, dataCaptured: ['response_time_per_item', 'error_types', 'confidence_indicators'] },
  2:  { primaryInteraction: 'mcq', secondaryInteraction: 'free_text', interactionConfig: { mcqType: 'single_select', optionCount: 4, freeTextPurpose: 'justify_decision', freeTextLength: 'moderate' }, dataCaptured: ['factors_considered', 'logic_flow', 'trade_off_articulation'] },
  3:  { primaryInteraction: 'free_text', secondaryInteraction: null, interactionConfig: { freeTextPurpose: 'identify_gaps', freeTextLength: 'detailed', structuredPrompts: ['what_is_missing', 'what_is_present'] }, dataCaptured: ['gap_categories_identified', 'precision', 'recall'] },
  4:  { primaryInteraction: 'free_text', secondaryInteraction: null, interactionConfig: { freeTextPurpose: 'causal_chain', freeTextLength: 'detailed', structuredPrompts: ['surface_symptom', 'why_chain', 'root_cause'] }, dataCaptured: ['depth_of_analysis', 'why_steps_count', 'logical_validity'] },
  5:  { primaryInteraction: 'mcq', secondaryInteraction: 'free_text', interactionConfig: { mcqType: 'multi_select', optionCount: 5, freeTextPurpose: 'evaluate_evidence', freeTextLength: 'moderate' }, dataCaptured: ['questions_asked', 'evidence_cited', 'assumption_categories'] },
  6:  { primaryInteraction: 'timed_text', secondaryInteraction: null, interactionConfig: { timerDuration: 300, constraintShiftAt: 150, freeTextLength: 'moderate' }, dataCaptured: ['time_to_adapt', 'adapted_solution_quality', 'pivot_count'] },
  7:  { primaryInteraction: 'timed_text', secondaryInteraction: null, interactionConfig: { timerDuration: 300, countingMetric: 'ideas', freeTextLength: 'unlimited' }, dataCaptured: ['ideas_per_minute', 'category_spread', 'novelty_ratings'] },
  8:  { primaryInteraction: 'free_text', secondaryInteraction: null, interactionConfig: { freeTextPurpose: 'synthesis_report', freeTextLength: 'detailed' }, dataCaptured: ['sources_integrated', 'accuracy', 'coherence', 'insight_quality'] },
  9:  { primaryInteraction: 'ranked_list', secondaryInteraction: 'free_text', interactionConfig: { itemCount: 6, freeTextPurpose: 'justify_ranking', freeTextLength: 'brief' }, dataCaptured: ['criteria_applied', 'ranking_accuracy', 'justification_quality'] },
  10: { primaryInteraction: 'free_text', secondaryInteraction: null, interactionConfig: { freeTextPurpose: 'draft_message', freeTextLength: 'detailed' }, dataCaptured: ['structure_adherence', 'persuasion_elements', 'clarity_metrics'] },
  11: { primaryInteraction: 'video_capture', secondaryInteraction: null, interactionConfig: { maxDuration: 120, cameraRequired: true }, dataCaptured: ['eye_contact_pct', 'pace_wpm', 'filler_word_count', 'gesture_frequency', 'posture_score'] },
  12: { primaryInteraction: 'dialogue_simulation', secondaryInteraction: null, interactionConfig: { maxDuration: 300, dialogueTurns: 5, requiredFields: ['scenario_id', 'character_name', 'role', 'personality', 'situation', 'objective', 'greeting', 'behavioral_rules', 'difficulty_modifiers'] }, dataCaptured: ['response_latency', 'tone_matching', 'question_quality', 'active_listening_indicators'] },
  13: { primaryInteraction: 'free_text', secondaryInteraction: null, interactionConfig: { freeTextPurpose: 'structured_reflection', freeTextLength: 'detailed', structuredPrompts: ['what_happened', 'what_i_learned', 'what_i_will_do'] }, dataCaptured: ['reflection_specificity', 'action_commitment_quality', 'pattern_recognition'] },
  14: { primaryInteraction: 'free_text', secondaryInteraction: 'mcq', interactionConfig: { freeTextPurpose: 'create_plan', freeTextLength: 'detailed', mcqType: 'multi_select', optionCount: 5 }, dataCaptured: ['goal_specificity', 'stakeholder_coverage', 'timeline_realism'] },
  15: { primaryInteraction: 'free_text', secondaryInteraction: null, interactionConfig: { freeTextPurpose: 'rewrite_for_audience', freeTextLength: 'detailed' }, dataCaptured: ['reading_level_change', 'concept_preservation', 'clarity_metrics'] },
};

// ── Client-side Prompt Preview Builder ──

export function buildPromptPreview(form: ClassCreationForm, discoveryMode: boolean): string {
  const engine = ENGINE_TYPES.find((e) => e.id === form.engine_type);
  if (!engine) return '⚠ Select a plot in Step 1 to preview the prompt.';

  const interaction = ENGINE_INTERACTION_MAP[engine.id];
  if (!interaction) return '⚠ No interaction map found for engine ' + engine.id;

  const plotMapping = PLOT_SKILL_MAP.find((p) => p.id === engine.id);
  const skills = discoveryMode
    ? (plotMapping?.skills ?? [])
    : form.skills_assessed;

  const cardMetadata = JSON.stringify({
    engineType: engine.id,
    engineName: engine.name,
    skillsAssessed: skills,
    difficulty: form.difficulty,
    durationMinutes: form.duration_minutes,
    industry: form.industry || '(not set)',
    functionalContext: form.functional_context || '(not set)',
    frameworks: plotMapping?.primaryFrameworks ?? '(none)',
    discoveryMode,
  }, null, 2);

  const workshopOnlySkills = [...NON_ASSESSABLE_SKILLS].join(', ');

  // ── Preview of playbook-backend/prompts/content_generation_prompt.md ──
  // The actual prompt is loaded from the .md template by the Python backend.
  // This preview approximates the final rendered prompt.

  const frameworks = plotMapping?.primaryFrameworks ?? '(none)';
  const industry = form.industry || 'General';
  const fn = form.functional_context || 'General';

  const genModeBlock = discoveryMode
    ? `## GENERATION MODE: DISCOVERY
You are generating a micro-class WITHOUT a pre-assigned skill list. Focus entirely on creating the most pedagogically compelling scenario possible for the given engine type and context. Do NOT try to target any specific skills.

In your output, set skillsAssessed to an empty array: "skillsAssessed": []
A separate audit will identify which L4 skills this content naturally assesses.`
    : `## TARGET SKILLS (${skills.length})
Use ONLY these exact skill IDs in the output — do not invent, abbreviate, or rename them:
${skills.map((s: string) => '- ' + s).join('\n')}

## SKILL DESIGN REQUIREMENTS (DIRECTED MODE)
For EACH skill listed above, design the scenario so that a learner cannot fully succeed without demonstrating that skill. Include a "skillDesignRationale" object in your output.`;

  return `# Micro-Class Content Generation Prompt

You are an expert instructional designer building micro-classes for the Playbook professional skills development platform. Your job is to generate one complete, fully playable micro-class that a learner can complete in ${form.duration_minutes} minutes.

---

## MICRO-CLASS TYPE

Name: Engine ${engine.id} — ${engine.name}
Cognitive Physics: ${engine.description}
Analytical Tension: (populated at generation time from engine map)
Duration: ${form.duration_minutes} minutes
Scoring Method: (populated at generation time from engine map)

---

## INPUTS

### Card Metadata
\`\`\`json
${cardMetadata}
\`\`\`

### Engine Type: ${engine.id} — ${engine.name}
- **Input types**: ${engine.interaction_types.join(', ')}
- **What's scored**: ${interaction.dataCaptured.join(', ')}
- **Diagnostic output**: Per-skill rubric scores with evidence
- **AI role**: AI scorer analyses learner response against rubric criteria
- **Data captured**: ${interaction.dataCaptured.join(', ')}

### Response Interaction
- **Primary**: ${interaction.primaryInteraction}
- **Secondary**: ${interaction.secondaryInteraction ?? 'none'}
- **Config**: ${JSON.stringify(interaction.interactionConfig, null, 2)}

### Framework Selection
Recommended frameworks: ${frameworks}
Choose 1-2 frameworks from the list above that best fit this engine's plot AND scenario. Use ONLY real, established frameworks — do NOT invent framework names.

### Skill Eligibility
**IMPORTANT:** Only the skills listed in \`skillsAssessed\` from the card metadata may be used. Do NOT introduce additional skills — some require a live counterpart.

Workshop-only skills: ${workshopOnlySkills}

${genModeBlock}

### Skill Rubrics
(Loaded from rubric database at generation time)

### Framework Definition
Primary: ${frameworks}
(Full definitions loaded at generation time)

---

## CONFIGURATION
- Difficulty: ${form.difficulty}
- Industry: ${industry}
- Business Function: ${fn}

## CRITICAL: INDUSTRY GROUNDING RULE
${industry !== 'General' ? 'The scenario MUST be set in the **' + industry + '** industry. Characters, company, products, metrics, and terminology must all be authentic to ' + industry + '.' : 'If no specific industry is selected, choose a realistic professional scenario that fits naturally.'}

## CRITICAL: DIFFICULTY GROUNDING RULE
(Concrete Beginner/Intermediate/Advanced parameters populated at generation time — info volume, interaction count, distractor quality, ambiguity, scaffolding, time pressure, constraint shifts)

## CRITICAL: BUSINESS FUNCTION GROUNDING RULE
${fn !== 'General' ? 'The protagonist MUST work in a **' + fn + '** role with role-specific responsibilities, KPIs, and vocabulary.' : 'If no specific business function is selected, choose a role that fits the scenario naturally.'}

## CRITICAL: SELF-CONTAINED CONTENT RULE
ALL data the learner needs MUST be written inline. NEVER reference external materials.

---

## TASK

Generate a complete content JSON with exactly 5 sections. The scenario must be pedagogically compelling, realistic, and grounded in the industry, difficulty, and business function specified above.

(Section requirements, interaction type JSON schemas, output format, and critical reminders are included in the full prompt sent to the LLM — see playbook-backend/prompts/content_generation_prompt.md)

Return ONLY the JSON. No commentary.`;
}
